/*
 * Where Twin entities live in the app: each entity's own page, and the directory in Knowledge's Entities folder.
 * Plain links, so a server page can redirect to them as a client one links to them.
 */

/** One of an entity's relationship groups, as its detail's `linked` entry names it. */
export type TwinEntityGroup = { linkedTo: string; linkType: string; direction: "outgoing" | "incoming" };

/** Knowledge's selection for its Entities folder, where the entity directory lives. */
export const TWIN_ENTITIES_KNOWLEDGE_ITEM = JSON.stringify(["resource-folder", "entityTypes"]);
/** The directory's own URL parameters, which Knowledge drops when another item is opened. */
export const TWIN_ENTITY_DIRECTORY_PARAMS = ["type", "q", "sort", "linkedTo", "linkType", "direction"] as const;

/** Knowledge's selection for one entity type, the folder under Entities that lists its entities. */
export function twinEntityTypeKnowledgeItem(type: string): string {
  return JSON.stringify(["twin-entity-type", type]);
}

/** Whether a Knowledge selection lists entities, where the directory's own parameters belong. */
export function isTwinEntityDirectoryItem(key: string | null): boolean {
  return key === TWIN_ENTITIES_KNOWLEDGE_ITEM || Boolean(key?.startsWith('["twin-entity-type",'));
}

export function setTwinEntityGroup(params: URLSearchParams, group: TwinEntityGroup | null | undefined) {
  if (!group) return;
  params.set("linkedTo", group.linkedTo);
  params.set("linkType", group.linkType);
  params.set("direction", group.direction);
}

/** Knowledge's selection for one entity, shown under its Entities folder. */
export function twinEntityKnowledgeItem(entityId: string): string {
  return JSON.stringify(["twin-entity", entityId]);
}

/** The entity a Knowledge selection names, or null. */
export function twinEntityIdFromKnowledgeItem(key: string | null): string | null {
  if (!key) return null;
  try {
    const parts: unknown = JSON.parse(key);
    return Array.isArray(parts) && parts.length === 2 && parts[0] === "twin-entity" && typeof parts[1] === "string"
      && /^ent_[a-f0-9]{24}$/u.test(parts[1]) ? parts[1] : null;
  } catch {
    return null;
  }
}

/** An entity's page, in Knowledge. */
export function twinEntityHref(entityId: string): string {
  return `/knowledge?${new URLSearchParams({ item: twinEntityKnowledgeItem(entityId) }).toString()}`;
}

/**
 * The entity directory, in Knowledge: the Entities folder, or one type's folder beneath it, optionally on one
 * entity's relationship group.
 */
export function twinEntitiesHref(type?: string | null, group?: TwinEntityGroup | null): string {
  const params = new URLSearchParams({ item: type ? twinEntityTypeKnowledgeItem(type) : TWIN_ENTITIES_KNOWLEDGE_ITEM });
  setTwinEntityGroup(params, group);
  return `/knowledge?${params.toString()}`;
}
